import React, { useState } from 'react';
import Navbar from '../components/Navbar';
import Footer from './Footer';
import './FAQ.css';

// ─── DATA (mirrors mobile faq.tsx) ───

const FAQ_DATA = [
  {
    category: 'HRV Basics',
    question: 'What is Heart Rate Variability (HRV)?',
    answer:
      'HRV measures the variation in time between each heartbeat. A higher HRV generally indicates better cardiovascular fitness and stress resilience, while a lower HRV can indicate stress, fatigue, or illness.',
  },
  {
    category: 'HRV Basics',
    question: 'What is a good HRV score?',
    answer:
      'HRV varies greatly between individuals based on age, fitness level, and genetics. Rather than comparing to others, focus on your personal baseline. The app tracks your trends over time to help you understand what is normal for you.',
  },
  {
    category: 'HRV Basics',
    question: 'When should I measure my HRV?',
    answer:
      'For the most consistent results, measure your HRV first thing in the morning before getting out of bed. This gives a resting baseline that is less affected by daily activities, caffeine, or stress.',
  },
  {
    category: 'Using the App',
    question: 'How do I connect my sensor?',
    answer:
      'Sensor pairing is handled in the HRV-4 mobile app. Open the app, go to the Sensors tab, ensure Bluetooth is enabled, and tap "Scan" to search for nearby BLE devices. Currently the app supports Polar Loop and Polar Verity Sense for tracking HRV data.',
  },
  {
    category: 'Using the App',
    question: 'What does the Biological Age score mean?',
    answer:
      'Your Biological Age is an estimate based on your HRV data, activity levels, and recovery patterns. It reflects how well your body is functioning compared to your chronological age. A lower biological age indicates better overall health.',
  },
  {
    category: 'Using the App',
    question: 'How are my Quick Actions calculated?',
    answer:
      'Quick Actions like Activity, Sleep, and Meditation are personalized suggestions based on your HRV trends, recovery status, and activity history. They help you make decisions that support your well-being.',
  },
  {
    category: 'Health & Recovery',
    question: 'How does sleep affect my HRV?',
    answer:
      'Quality sleep is one of the strongest positive influences on HRV. Nights with 7 or more hours of deep sleep typically result in higher HRV the following morning. The app tracks this correlation for you.',
  },
  {
    category: 'Health & Recovery',
    question: 'Why is my HRV low today?',
    answer:
      'Low HRV can be caused by poor sleep, high stress, intense exercise the previous day, alcohol consumption, illness, or dehydration. Check your recent activity log and sleep data for patterns.',
  },
  {
    category: 'Health & Recovery',
    question: 'What is the Burnout Resilience score?',
    answer:
      'Burnout Resilience reflects your body’s ability to handle sustained stress based on HRV patterns over weeks. A higher score means your autonomic nervous system is recovering well between stressors.',
  },
  {
    category: 'Account',
    question: 'How do I change my password?',
    answer:
      'Go to your Profile from the top-right icon on the home screen, then tap Settings. You can update your email and password from there.',
  },
  {
    category: 'Account',
    question: 'Is my health data secure?',
    answer:
      'Yes. All your health data is encrypted in transit and at rest. We never share your personal health information with third parties. You can request data deletion at any time from Settings.',
  },
];

const SUPPORT_EMAIL = 'hrv4hrv4@gmail.com';

// ─── ICONS ───

function HelpCircleIcon({ size = 32, color = '#5CB89A' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  );
}

function ChevronDownIcon({ size = 20, color = '#A0ABA8', flipped = false }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ transform: flipped ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s ease' }}
    >
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}

function MailIcon({ size = 18, color = '#FFFFFF' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
      <polyline points="22,6 12,13 2,6" />
    </svg>
  );
}

function SearchIcon({ size = 18, color = '#A0ABA8' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="11" cy="11" r="8" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  );
}

function ClearIcon({ size = 16, color = '#A0ABA8' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <line x1="15" y1="9" x2="9" y2="15" />
      <line x1="9" y1="9" x2="15" y2="15" />
    </svg>
  );
}

// ─── HELPERS ───

function groupByCategory(items) {
  const map = {};
  items.forEach((item) => {
    if (!map[item.category]) map[item.category] = [];
    map[item.category].push(item);
  });
  return Object.entries(map);
}

function openSupportEmail() {
  const subject = encodeURIComponent('HRV4 Support Request');
  const body = encodeURIComponent(
    `Hello HRV4 Support Team,\n\nI need help with:\n\n[Please describe your issue here]\n\n---\nApp: HRV-4\nPlatform: Web\n`
  );
  const mailtoUrl = `mailto:${SUPPORT_EMAIL}?subject=${subject}&body=${body}`;
  window.location.href = mailtoUrl;
}

// ─── ACCORDION ITEM ───

function AccordionItem({ item, isOpen, onToggle }) {
  return (
    <div className={`faq-accordion-item ${isOpen ? 'is-open' : ''}`}>
      <button
        type="button"
        className="faq-question-row"
        onClick={onToggle}
        aria-expanded={isOpen}
      >
        <span className="faq-question-text">{item.question}</span>
        <ChevronDownIcon size={20} color="#A0ABA8" flipped={isOpen} />
      </button>
      <div className="faq-answer-wrap" style={{ maxHeight: isOpen ? '600px' : '0px' }}>
        <p className="faq-answer-text">{item.answer}</p>
      </div>
    </div>
  );
}

// ─── MAIN ───

function FAQ() {
  const [openKey, setOpenKey] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Filter by search
  const filtered = searchQuery.trim()
    ? FAQ_DATA.filter((item) => {
        const q = searchQuery.toLowerCase();
        return (
          item.question.toLowerCase().includes(q) ||
          item.answer.toLowerCase().includes(q) ||
          item.category.toLowerCase().includes(q)
        );
      })
    : FAQ_DATA;

  const grouped = groupByCategory(filtered);

  return (
    <div className="faq-page-wrapper">
      <Navbar />

      <div className="faq-container">
        {/* ── Intro Card ── */}
        <div className="faq-card faq-intro-card">
          <div className="faq-intro-icon-wrap">
            <HelpCircleIcon size={32} color="#5CB89A" />
          </div>
          <h1 className="faq-intro-title">How can we help?</h1>
          <p className="faq-intro-subtitle">
            Find answers to common questions about HRV tracking, using the app, and improving your health.
          </p>

          {/* Search bar */}
          <div className="faq-search-wrap">
            <span className="faq-search-icon">
              <SearchIcon size={18} color="#A0ABA8" />
            </span>
            <input
              type="text"
              className="faq-search-input"
              placeholder="Search questions..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button
                type="button"
                className="faq-search-clear"
                onClick={() => setSearchQuery('')}
                aria-label="Clear search"
              >
                <ClearIcon size={16} color="#A0ABA8" />
              </button>
            )}
          </div>
        </div>

        {/* ── FAQ Sections ── */}
        {grouped.length === 0 ? (
          <div className="faq-card faq-empty">
            <p>No questions match your search.</p>
            <button
              type="button"
              className="faq-empty-btn"
              onClick={() => setSearchQuery('')}
            >
              Clear search
            </button>
          </div>
        ) : (
          grouped.map(([category, items]) => (
            <div key={category} className="faq-card faq-section-card">
              <h2 className="faq-section-title">{category}</h2>
              {items.map((item, index) => {
                const key = `${category}-${index}`;
                return (
                  <AccordionItem
                    key={key}
                    item={item}
                    isOpen={openKey === key}
                    onToggle={() => setOpenKey(openKey === key ? null : key)}
                  />
                );
              })}
            </div>
          ))
        )}

        {/* ── Contact Card ── */}
        <div className="faq-card faq-contact-card">
          <h2 className="faq-contact-title">Still have questions?</h2>
          <p className="faq-contact-text">
            Reach out to our support team and we’ll get back to you as soon as possible.
          </p>
          <button
            type="button"
            className="faq-contact-btn"
            onClick={openSupportEmail}
          >
            <MailIcon size={18} color="#FFFFFF" />
            <span>Contact Support</span>
          </button>
          <span className="faq-email-hint">{SUPPORT_EMAIL}</span>
        </div>
      </div>

      <Footer />
    </div>
  );
}

export default FAQ;